
import {
  useEffect,
  useState
} from "react";

import {
  useLocation,
  useNavigate
} from "react-router-dom";

import Storage from "../Storage/Storage.jsx";


// =====================================================
// API BASE
// =====================================================

const API_BASE =
  "https://td9dmwg0-8000.inc1.devtunnels.ms";


// =====================================================
// DASHBOARD
// =====================================================

function Dashboard({ onLogout }) {

  // ===================================================
  // ROUTER
  // ===================================================

  const navigate =
    useNavigate();

  const location =
    useLocation();


  // ===================================================
  // PAGE
  // ===================================================

  const [activePage, setActivePage] =
    useState("dashboard");


  // ===================================================
  // SECTION
  // ===================================================

  const [activeSection, setActiveSection] =
    useState("storage");


  // ===================================================
  // HEADER
  // ===================================================

  const [headerVisible, setHeaderVisible] =
    useState(true);


  // ===================================================
  // USER
  // ===================================================

  const [user, setUser] =
    useState({
      username: "",
      email: ""
    });


  const [loading, setLoading] =
    useState(true);


  // ===================================================
  // LOGOUT
  // ===================================================

  const [logoutLoading, setLogoutLoading] =
    useState(false);

  const [logoutError, setLogoutError] =
    useState("");


  // =====================================================
  // NAVIGATE TO LOGIN
  // =====================================================

  const goToLogin = () => {

    console.log(
      "Navigating to login..."
    );

    if (
      typeof onLogout ===
      "function"
    ) {

      onLogout();

      return;
    }

    navigate(
      "/login",
      {
        replace: true
      }
    );
  };


  // =====================================================
  // SYNC UI WITH URL
  // =====================================================

  useEffect(() => {

    const path =
      location.pathname;


    console.log(
      "Current route:",
      path
    );


    // ================================================
    // DASHBOARD
    // ================================================

    if (
      path === "/dashboard" ||
      path === "/"
    ) {

      setActivePage(
        "dashboard"
      );

      setActiveSection(
        "storage"
      );

      setHeaderVisible(
        true
      );

      return;
    }


    // ================================================
    // STORAGE
    // ================================================

    if (
      path === "/storage" ||
      path.startsWith(
        "/storage/folders/"
      )
    ) {

      setActivePage(
        "dashboard"
      );

      setActiveSection(
        "storage"
      );

      setHeaderVisible(
        false
      );

      return;
    }


    // ================================================
    // PHOTOS
    // ================================================

    if (
      path === "/photos"
    ) {

      setActivePage(
        "dashboard"
      );

      setActiveSection(
        "photos"
      );

      setHeaderVisible(
        false
      );

      return;
    }


    // ================================================
    // SONGS
    // ================================================

    if (
      path === "/songs"
    ) {

      setActivePage(
        "dashboard"
      );

      setActiveSection(
        "songs"
      );

      setHeaderVisible(
        false
      );

      return;
    }


    // ================================================
    // CHAT
    // ================================================

    if (
      path === "/chat"
    ) {

      setActivePage(
        "dashboard"
      );

      setActiveSection(
        "chat"
      );

      setHeaderVisible(
        false
      );

      return;
    }


    // ================================================
    // PROFILE
    // ================================================

    if (
      path === "/profile"
    ) {

      setActivePage(
        "profile"
      );

      setActiveSection(
        "storage"
      );

      setHeaderVisible(
        true
      );

      return;
    }


    // ================================================
    // SETTINGS
    // ================================================

    if (
      path === "/settings"
    ) {

      setActivePage(
        "settings"
      );

      setActiveSection(
        "storage"
      );

      setHeaderVisible(
        true
      );

      return;
    }

  }, [
    location.pathname
  ]);


  // =====================================================
  // GET CURRENT USER
  // =====================================================

  useEffect(() => {

    let cancelled =
      false;


    const controller =
      new AbortController();


    const timeout =
      setTimeout(() => {

        console.warn(
          "User request timeout"
        );

        controller.abort();

      }, 15000);


    const getUser =
      async () => {

        try {

          console.log(
            "Fetching current user..."
          );


          const response =
            await fetch(
              `${API_BASE}/auth/me`,
              {
                method: "GET",

                credentials:
                  "include",

                cache:
                  "no-store",

                headers: {
                  Accept:
                    "application/json"
                },

                signal:
                  controller.signal
              }
            );


          if (
            cancelled
          ) {

            return;
          }


          let data = {};


          try {

            data =
              await response.json();

          } catch {

            data = {};

          }


          console.log(
            "User response:",
            response.status,
            data
          );


          if (
            response.ok
          ) {

            setUser({

              username:
                data.username ||
                "",

              email:
                data.email ||
                ""

            });

          } else {

            console.warn(
              "User fetch failed:",
              response.status
            );


            // =========================================
            // DO NOT IMMEDIATELY LOGOUT HERE
            // =========================================

            if (
              response.status ===
              401
            ) {

              console.warn(
                "Authentication may have expired."
              );

            }

          }

        } catch (
          error
        ) {

          if (
            cancelled
          ) {

            return;
          }


          if (
            error.name ===
            "AbortError"
          ) {

            console.warn(
              "User request timed out."
            );

          } else {

            console.error(
              "User fetch error:",
              error
            );

          }

        } finally {

          clearTimeout(
            timeout
          );


          if (
            !cancelled
          ) {

            setLoading(
              false
            );

          }

        }

      };


    getUser();


    return () => {

      cancelled =
        true;

      controller.abort();

      clearTimeout(
        timeout
      );

    };

  }, []);


  // =====================================================
  // GET CSRF TOKEN
  // =====================================================

  const getCsrfToken =
    async () => {

      // =================================================
      // FIRST: CHECK EXISTING COOKIE
      // =================================================

      const cookies =
        document.cookie
          .split(";")
          .map(
            (cookie) =>
              cookie.trim()
          );


      const csrfCookie =
        cookies.find(
          (cookie) =>
            cookie.startsWith(
              "csrf="
            )
        );


      if (
        csrfCookie
      ) {

        const token =
          csrfCookie.substring(
            "csrf=".length
          );


        if (
          token
        ) {

          console.log(
            "CSRF cookie found."
          );

          return token;
        }
      }


      // =================================================
      // COOKIE NOT FOUND
      // GET A NEW CSRF TOKEN
      // =================================================

      console.log(
        "CSRF cookie not found."
      );

      console.log(
        "Requesting /auth/csrf..."
      );


      const response =
        await fetch(
          `${API_BASE}/auth/csrf`,
          {
            method: "GET",

            credentials:
              "include",

            cache:
              "no-store",

            headers: {
              Accept:
                "application/json"
            }
          }
        );


      console.log(
        "CSRF response status:",
        response.status
      );


      if (
        !response.ok
      ) {

        throw new Error(
          `Unable to get CSRF token (${response.status})`
        );
      }


      const data =
        await response.json();


      console.log(
        "CSRF response:",
        data
      );


      const token =
        data.csrf_token ||
        data.csrf;


      if (
        !token
      ) {

        throw new Error(
          "CSRF token was not returned by server."
        );
      }


      return token;
    };


  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout =
    async () => {

      if (
        logoutLoading
      ) {

        return;
      }


      setLogoutLoading(
        true
      );

      setLogoutError(
        ""
      );


      try {

        console.log(
          "================================"
        );

        console.log(
          "STARTING LOGOUT"
        );

        console.log(
          "================================"
        );


        // =================================================
        // GET CSRF TOKEN
        // =================================================

        let csrfToken;


        try {

          csrfToken =
            await getCsrfToken();

        } catch (
          csrfError
        ) {

          console.error(
            "CSRF token error:",
            csrfError
          );

          throw csrfError;
        }


        if (
          !csrfToken
        ) {

          throw new Error(
            "CSRF token is empty."
          );
        }


        console.log(
          "CSRF token available:",
          true
        );


        // =================================================
        // SEND LOGOUT REQUEST
        // =================================================

        console.log(
          "Sending POST /auth/logout..."
        );

        console.log(
          "X-CSRF-Token header will be sent."
        );


        const response =
          await fetch(
            `${API_BASE}/auth/logout`,
            {
              method: "POST",

              credentials:
                "include",

              cache:
                "no-store",

              headers: {

                Accept:
                  "application/json",

                "X-CSRF-Token":
                  csrfToken

              }
            }
          );


        console.log(
          "Logout HTTP status:",
          response.status
        );


        // =================================================
        // READ RESPONSE
        // =================================================

        let data = {};


        const contentType =
          response.headers.get(
            "content-type"
          ) || "";


        if (
          contentType.includes(
            "application/json"
          )
        ) {

          try {

            data =
              await response.json();

          } catch {

            data = {};

          }

        } else {

          try {

            const text =
              await response.text();

            data = {
              detail: text
            };

          } catch {

            data = {};

          }

        }


        console.log(
          "Logout response:",
          data
        );


        // =================================================
        // SUCCESS
        // =================================================

        if (
          response.ok
        ) {

          console.log(
            "================================"
          );

          console.log(
            "LOGOUT SUCCESS"
          );

          console.log(
            "================================"
          );


          // ===============================================
          // CLEAR LOCAL STORAGE
          // ===============================================

          try {

            localStorage.clear();

          } catch (
            storageError
          ) {

            console.warn(
              "localStorage clear failed:",
              storageError
            );

          }


          // ===============================================
          // CLEAR SESSION STORAGE
          // ===============================================

          try {

            sessionStorage.clear();

          } catch (
            storageError
          ) {

            console.warn(
              "sessionStorage clear failed:",
              storageError
            );

          }


          // ===============================================
          // RESET USER
          // ===============================================

          setUser({
            username: "",
            email: ""
          });


          // ===============================================
          // GO LOGIN
          // ===============================================

          goToLogin();

          return;
        }


        // =================================================
        // 401 / 403
        // =================================================

        if (
          response.status ===
            401 ||
          response.status ===
            403
        ) {

          console.warn(
            "Session or CSRF already invalid."
          );

          console.warn(
            "Clearing client session."
          );


          try {

            localStorage.clear();

          } catch {}


          try {

            sessionStorage.clear();

          } catch {}


          setUser({
            username: "",
            email: ""
          });


          goToLogin();

          return;
        }


        // =================================================
        // OTHER SERVER ERROR
        // =================================================

        setLogoutError(
          data.detail ||
          data.message ||
          `Logout failed (${response.status})`
        );

      } catch (
        error
      ) {

        console.error(
          "Logout error:",
          error
        );


        // =================================================
        // SERVER UNREACHABLE
        // =================================================

        try {

          localStorage.clear();

        } catch {}


        try {

          sessionStorage.clear();

        } catch {}


        setUser({
          username: "",
          email: ""
        });


        setLogoutError(
          "Server connection failed. Local session cleared."
        );


        // ===============================================
        // NAVIGATE TO LOGIN
        // ===============================================

        setTimeout(() => {

          goToLogin();

        }, 500);

      } finally {

        setLogoutLoading(
          false
        );

      }

    };


  // =====================================================
  // SECTION CHANGE
  // =====================================================

  const handleSectionChange =
    (section) => {

      console.log(
        "Changing section:",
        section
      );


      setHeaderVisible(
        false
      );


      // ==============================================
      // STORAGE
      // ==============================================

      if (
        section ===
        "storage"
      ) {

        navigate(
          "/storage"
        );

        return;
      }


      // ==============================================
      // PHOTOS
      // ==============================================

      if (
        section ===
        "photos"
      ) {

        navigate(
          "/photos"
        );

        return;
      }


      // ==============================================
      // SONGS
      // ==============================================

      if (
        section ===
        "songs"
      ) {

        navigate(
          "/songs"
        );

        return;
      }


      // ==============================================
      // CHAT
      // ==============================================

      if (
        section ===
        "chat"
      ) {

        navigate(
          "/chat"
        );

        return;
      }

    };


  // =====================================================
  // SIDEBAR PAGE CHANGE
  // =====================================================

  const handlePageChange =
    (page) => {

      console.log(
        "Changing page:",
        page
      );


      // ==============================================
      // DASHBOARD
      // ==============================================

      if (
        page ===
        "dashboard"
      ) {

        navigate(
          "/dashboard"
        );

        return;
      }


      // ==============================================
      // PROFILE
      // ==============================================

      if (
        page ===
        "profile"
      ) {

        navigate(
          "/profile"
        );

        return;
      }


      // ==============================================
      // SETTINGS
      // ==============================================

      if (
        page ===
        "settings"
      ) {

        navigate(
          "/settings"
        );

        return;
      }

    };


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div className="dashboard">


      {/* ================================================= */}
      {/* SIDEBAR */}
      {/* ================================================= */}

      <aside className="dashboard-sidebar">


        {/* =============================================== */}
        {/* LOGO */}
        {/* =============================================== */}

        <div className="dashboard-logo">

          <div className="logo-icon">
            ⚡
          </div>

          <span>
            SecureApp
          </span>

        </div>


        {/* =============================================== */}
        {/* NAVIGATION */}
        {/* =============================================== */}

        <nav className="dashboard-nav">


          {/* ============================================= */}
          {/* DASHBOARD */}
          {/* ============================================= */}

          <button
            className={`nav-item ${
              activePage ===
              "dashboard"
                ? "active"
                : ""
            }`}
            onClick={() =>
              handlePageChange(
                "dashboard"
              )
            }
            disabled={
              logoutLoading
            }
          >

            <span>
              ⌂
            </span>

            Dashboard

          </button>


          {/* ============================================= */}
          {/* PROFILE */}
          {/* ============================================= */}

          <button
            className={`nav-item ${
              activePage ===
              "profile"
                ? "active"
                : ""
            }`}
            onClick={() =>
              handlePageChange(
                "profile"
              )
            }
            disabled={
              logoutLoading
            }
          >

            <span>
              ◉
            </span>

            Profile

          </button>


          {/* ============================================= */}
          {/* SETTINGS */}
          {/* ============================================= */}

          <button
            className={`nav-item ${
              activePage ===
              "settings"
                ? "active"
                : ""
            }`}
            onClick={() =>
              handlePageChange(
                "settings"
              )
            }
            disabled={
              logoutLoading
            }
          >

            <span>
              ⚙
            </span>

            Settings

          </button>


        </nav>


        {/* =============================================== */}
        {/* LOGOUT */}
        {/* =============================================== */}

        <button
          className="logout-btn"
          onClick={
            handleLogout
          }
          disabled={
            logoutLoading
          }
        >

          <span>
            ↪
          </span>

          {logoutLoading
            ? "Logging out..."
            : "Logout"
          }

        </button>


        {/* =============================================== */}
        {/* LOGOUT ERROR */}
        {/* =============================================== */}

        {logoutError && (

          <div className="logout-error">

            {logoutError}

          </div>

        )}


      </aside>


      {/* ================================================= */}
      {/* MAIN */}
      {/* ================================================= */}

      <main className="dashboard-main">


        {/* ================================================= */}
        {/* DASHBOARD CONTENT */}
        {/* ================================================= */}

        {activePage ===
          "dashboard" && (

          <>


            {/* ============================================= */}
            {/* INTRO */}
            {/* ============================================= */}

            <div
              className={`dashboard-intro ${
                headerVisible
                  ? "show"
                  : "hide"
              }`}
            >

              <p className="dashboard-label">
                OVERVIEW
              </p>


              <h1>
                Dashboard
              </h1>


              <p className="dashboard-subtitle">
                Welcome back! Here's your workspace.
              </p>

            </div>


            {/* ============================================= */}
            {/* OPTIONS */}
            {/* ============================================= */}

            <div
              className={`dashboard-options ${
                headerVisible
                  ? "initial-options"
                  : "moved-options"
              }`}
            >


              {/* ========================================= */}
              {/* STORAGE */}
              {/* ========================================= */}

              <button
                className={
                  activeSection ===
                  "storage"
                    ? "selected"
                    : ""
                }
                onClick={() =>
                  handleSectionChange(
                    "storage"
                  )
                }
              >

                Storage

              </button>


              {/* ========================================= */}
              {/* PHOTOS */}
              {/* ========================================= */}

              <button
                className={
                  activeSection ===
                  "photos"
                    ? "selected"
                    : ""
                }
                onClick={() =>
                  handleSectionChange(
                    "photos"
                  )
                }
              >

                Photos

              </button>


              {/* ========================================= */}
              {/* SONGS */}
              {/* ========================================= */}

              <button
                className={
                  activeSection ===
                  "songs"
                    ? "selected"
                    : ""
                }
                onClick={() =>
                  handleSectionChange(
                    "songs"
                  )
                }
              >

                Songs

              </button>


              {/* ========================================= */}
              {/* CHAT */}
              {/* ========================================= */}

              <button
                className={
                  activeSection ===
                  "chat"
                    ? "selected"
                    : ""
                }
                onClick={() =>
                  handleSectionChange(
                    "chat"
                  )
                }
              >

                Chat

              </button>


            </div>


            {/* ============================================= */}
            {/* PROFILE CIRCLE */}
            {/* ============================================= */}

            <div className="dashboard-profile-circle">

              {user.username

                ? user.username
                    .charAt(0)
                    .toUpperCase()

                : "U"

              }

            </div>


            {/* ============================================= */}
            {/* STORAGE */}
            {/* ============================================= */}

            {activeSection ===
              "storage" && (

              <Storage />

            )}


            {/* ============================================= */}
            {/* PHOTOS */}
            {/* ============================================= */}

            {activeSection ===
              "photos" && (

              <section className="section-content">

                <div className="empty-icon">
                  ✦
                </div>

                <h2>
                  No photos yet
                </h2>

                <p>
                  Your photos will appear here.
                </p>

              </section>

            )}


            {/* ============================================= */}
            {/* SONGS */}
            {/* ============================================= */}

            {activeSection ===
              "songs" && (

              <section className="section-content">

                <div className="empty-icon">
                  ✦
                </div>

                <h2>
                  No songs yet
                </h2>

                <p>
                  Your songs will appear here.
                </p>

              </section>

            )}


            {/* ============================================= */}
            {/* CHAT */}
            {/* ============================================= */}

            {activeSection ===
              "chat" && (

              <section className="section-content">

                <div className="empty-icon">
                  ✦
                </div>

                <h2>
                  No conversations
                </h2>

                <p>
                  Start a new conversation.
                </p>

              </section>

            )}


          </>

        )}


        {/* ================================================= */}
        {/* PROFILE */}
        {/* ================================================= */}

        {activePage ===
          "profile" && (

          <section className="profile-page">


            <p className="dashboard-label">
              ACCOUNT
            </p>


            <h1>
              Profile
            </h1>


            <p className="dashboard-subtitle">
              Your account information
            </p>


            <div className="profile-card">


              <div className="large-profile-circle">

                {user.username

                  ? user.username
                      .charAt(0)
                      .toUpperCase()

                  : "U"

                }

              </div>


              {loading ? (

                <p>
                  Loading profile...
                </p>

              ) : (

                <>


                  <div className="profile-info">

                    <span>
                      Name
                    </span>

                    <strong>
                      {user.username}
                    </strong>

                  </div>


                  <div className="profile-info">

                    <span>
                      Email
                    </span>

                    <strong>
                      {user.email}
                    </strong>

                  </div>


                </>

              )}


            </div>


          </section>

        )}


        {/* ================================================= */}
        {/* SETTINGS */}
        {/* ================================================= */}

        {activePage ===
          "settings" && (

          <section className="profile-page">


            <p className="dashboard-label">
              ACCOUNT
            </p>


            <h1>
              Settings
            </h1>


            <p className="dashboard-subtitle">
              Manage your account settings.
            </p>


          </section>

        )}


      </main>


    </div>

  );

}


export default Dashboard;
